<template>
  <!--
    FINAL CHAT V2 -- the tutor's suggested next turns (upstream 0a347ba,
    design A16), drawn as V2's `.related-paths` card: «دوست داری از کدوم مسیر
    ادامه بدیم؟» over one card per suggestion (kind label, the suggestion's own
    label, an arrow). Every card comes from the tutor turn's `suggestions`;
    nothing here is a fixed V2 string. A pick sends the suggestion's id and the
    tutor runs the request it stored for it; the picked card keeps V2's
    `.selected` look while that turn is on its way, and every card is disabled
    meanwhile.
  -->
  <section
    class="ai-tutor-suggestions"
    data-testid="ai-tutor-suggestions"
    :aria-label="$t('aiTutorSetup.chat.suggestions.label')"
  >
    <div class="ai-tutor-suggestions__head">
      <div>
        <student-home-icon name="spark" :size="20" />
        <span>
          <strong>{{ $t('aiTutorSetup.chat.suggestions.title') }}</strong>
          <small>{{ $t('aiTutorSetup.chat.suggestions.hint') }}</small>
        </span>
      </div>
    </div>
    <div class="ai-tutor-suggestions__cards">
      <button
        v-for="suggestion in suggestions"
        :key="suggestion.id"
        type="button"
        :class="{ selected: suggestion.id === selectedId }"
        :data-testid="`ai-tutor-suggestion-${suggestion.id}`"
        :disabled="disabled"
        :aria-pressed="suggestion.id === selectedId ? 'true' : 'false'"
        @click="$emit('pick', suggestion)"
      >
        <span>{{ kindLabel(suggestion.kind) }}</span>
        <!-- CHAT-UX-01 delta (doc 29 §17.3 D-CX-9): the label is drawn as one
             line of Markdown + math (delimited or not), nothing clickable
             inside the button; the suggestion object itself, and what a
             pick sends (its id), are untouched. -->
        <strong>
          <markdown-content
            inline
            profile="student"
            :source="suggestion.label"
          />
        </strong>
        <student-home-icon name="arrow" :size="17" />
      </button>
    </div>
  </section>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import MarkdownContent from './MarkdownContent.vue'

const KINDS = ['next', 'check', 'deeper', 'broader', 'apply', 'clarify']

export default {
  name: 'ChatSuggestions',
  components: { StudentHomeIcon, MarkdownContent },
  props: {
    /** @type {import('vue').PropOptions<import('@/services/aiTutor/domain').TutorFollowUpSuggestion[]>} */
    suggestions: {
      type: Array,
      default: () => [],
    },
    // The suggestion whose turn is on its way (V2 `.selected`), if any.
    selectedId: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    kindLabel(kind) {
      const key = KINDS.includes(kind) ? kind : 'unknown'
      return this.$t(`aiTutorSetup.chat.suggestions.kinds.${key}`)
    },
  },
}
</script>

<style lang="scss" scoped>
// Exact values from the canonical V2 prototype (sha256 6ddb695a...):
// `.related-paths`, `.related-head`, `.related-paths button(.selected)`, and
// its <=620px rules. Inset by the avatar (34px) + row gap (9px) like the
// other cards under a tutor answer.
$v2-card-inset: 43px;

.ai-tutor-suggestions {
  margin: 0 $v2-card-inset;
  padding: 16px;
  border: 1px solid $borna-border;
  border-radius: 19px;
  background: #fff;
  line-height: 1.75;
}

.ai-tutor-suggestions__head > div {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  > ::v-deep svg {
    flex: none;
    color: $borna-indigo-600;
  }

  > span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: $borna-text;
    font-size: 11px;
  }

  small {
    color: $borna-muted;
    font-size: 9px;
  }
}

.ai-tutor-suggestions__cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
  margin-top: 12px;

  button {
    display: grid;
    grid-template-columns: 1fr 17px;
    align-content: center;
    gap: 4px 7px;
    min-width: 0;
    min-height: 82px;
    padding: 10px;
    border: 1px solid $borna-border;
    border-radius: 13px;
    background: $borna-canvas;
    color: $borna-text;
    font-family: inherit;
    line-height: 1.75;
    text-align: start;
    cursor: pointer;

    > span {
      grid-column: 1 / 3;
      color: $borna-muted;
      font-size: 8px;
    }

    > strong {
      min-width: 0;
      font-size: 9px;
      line-height: 1.65;
      overflow-wrap: anywhere;

      // UX2-04 (doc 30 §4, D-9): a formula in a suggestion label is pinned
      // to 14.5px -- the nearest size at which both bars of `=` stay
      // >= 100/255 at DPR 1 / 1.25 / 1.5 / 2 (measured on the real bundle;
      // every size from 9 to 14.25px fails at least one DPR). Larger than
      // the 9px label text by design: legibility of the relation glyph wins.
      ::v-deep .ai-md .katex {
        font-size: 14.5px;
      }
    }

    > ::v-deep svg {
      align-self: center;
      color: $borna-muted;
    }

    &.selected {
      border-color: $borna-indigo-600;
      background: $borna-indigo-50;

      > strong,
      > ::v-deep svg {
        color: $borna-indigo-900;
      }
    }

    &:disabled {
      cursor: not-allowed;
    }

    &:disabled:not(.selected) {
      opacity: 0.55;
    }
  }
}

// The arrow points the way the text ends (V2 draws it for RTL).
html:not(.is-rtl) .ai-tutor-suggestions__cards button > ::v-deep svg {
  transform: scaleX(-1);
}

// V2 `@media (width<=620px)`.
@media (max-width: 620px) {
  .ai-tutor-suggestions {
    margin-inline: 0;
  }

  .ai-tutor-suggestions__cards {
    grid-template-columns: 1fr;

    button {
      min-height: 68px;
    }
  }
}
</style>
